import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useLocalCopyToClipboard — EMAILS-IMPL-020
 *
 * A small, self-contained copy-to-clipboard hook, local to this feature
 * directory rather than importing features/agent-workspace/hooks/
 * useCopyToClipboard.js -- mirroring this module's own established
 * precedent (see components/CatalogTable.jsx's comment) of never reaching
 * into another workstream's code even when functionally identical, to
 * avoid an invasive cross-feature dependency for a ~20-line utility.
 *
 * Copies the EXACT string passed to `copy()` -- no trimming, no
 * transformation -- so callers copying a persisted Rendered Handoff
 * Snapshot value (including its literal `{{contact.id}}`/
 * `{{contact.state}}` GHL merge tokens) never have that value altered.
 */
export function useLocalCopyToClipboard(resetDelayMs = 1500) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copy = useCallback(
    async (value) => {
      try {
        if (!navigator?.clipboard?.writeText) {
          throw new Error("Clipboard API unavailable");
        }
        await navigator.clipboard.writeText(value);
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), resetDelayMs);
        return true;
      } catch {
        return false;
      }
    },
    [resetDelayMs]
  );

  return { copied, copy };
}

export default useLocalCopyToClipboard;
